import type { AuthUser } from '@/stores/authStore';
import { getServiceProviderForOwnerUser, getVendorForOwnerUser } from '@/api/catalog';
import { defaultPathForRoles, rolesIncludeAdmin } from '@/lib/auth';
import { sanitizeLoginNextPath } from '@/lib/loginNavigation';
import { vendorAwaitingAdminReview, vendorIsApproved } from '@/lib/vendorApproval';
import {
  parseSignupGoalsFromIntent,
  resolvePostSignupPath,
  type SignupGoals,
  SHOP_ONLY_GOALS,
} from '@/lib/signupGoals';
import { JOIN_PATH, isJoinPath } from '@/lib/joinPaths';

const NEXT_KEY = 'shopgnd-post-signup-next';
const GOALS_KEY = 'shopgnd-signup-goals';
const EMAIL_KEY = 'shopgnd-signup-email';
const PASSWORD_KEY = 'shopgnd-signup-password';
const BUSINESS_NAME_KEY = 'shopgnd-signup-business-name';
const LS_GOALS_KEY = 'shopgnd-signup-goals-backup';
const LS_NEXT_KEY = 'shopgnd-post-signup-next-backup';
const LS_EMAIL_KEY = 'shopgnd-signup-email-backup';
const LS_PASSWORD_KEY = 'shopgnd-signup-password-backup';
const LS_BUSINESS_NAME_KEY = 'shopgnd-signup-business-name-backup';

function storeVendorBusinessName(name: string | null | undefined): void {
  const trimmed = name?.trim();
  if (trimmed) {
    sessionStorage.setItem(BUSINESS_NAME_KEY, trimmed);
    localStorage.setItem(LS_BUSINESS_NAME_KEY, trimmed);
  } else {
    sessionStorage.removeItem(BUSINESS_NAME_KEY);
    localStorage.removeItem(LS_BUSINESS_NAME_KEY);
  }
}

export function persistVendorBusinessName(name: string): void {
  try {
    storeVendorBusinessName(name);
  } catch {
    /* private mode */
  }
}

export function getStoredVendorBusinessName(): string | null {
  try {
    return sessionStorage.getItem(BUSINESS_NAME_KEY) ?? localStorage.getItem(LS_BUSINESS_NAME_KEY);
  } catch {
    return null;
  }
}

function storeSignupPassword(password: string | null | undefined): void {
  if (password) {
    sessionStorage.setItem(PASSWORD_KEY, password);
    localStorage.setItem(LS_PASSWORD_KEY, password);
  } else {
    sessionStorage.removeItem(PASSWORD_KEY);
    localStorage.removeItem(LS_PASSWORD_KEY);
  }
}

export function persistSignupNavigation(
  email: string,
  goals: SignupGoals,
  nextPath: string,
  businessName?: string | null,
  password?: string,
): void {
  const safeNext = sanitizeLoginNextPath(nextPath);
  try {
    const normalizedEmail = email.trim().toLowerCase();
    sessionStorage.setItem(EMAIL_KEY, normalizedEmail);
    localStorage.setItem(LS_EMAIL_KEY, normalizedEmail);
    sessionStorage.setItem(GOALS_KEY, JSON.stringify(goals));
    localStorage.setItem(LS_GOALS_KEY, JSON.stringify(goals));
    if (safeNext) {
      sessionStorage.setItem(NEXT_KEY, safeNext);
      localStorage.setItem(LS_NEXT_KEY, safeNext);
    } else {
      sessionStorage.removeItem(NEXT_KEY);
      localStorage.removeItem(LS_NEXT_KEY);
    }
    storeVendorBusinessName(businessName);
    storeSignupPassword(password);
  } catch {
    /* private mode */
  }
}

/** Persist email verification session so refresh on /confirm-email can auto sign-in. */
export function persistConfirmEmailSession({
  email,
  password,
  nextPath,
  goals,
}: {
  email: string;
  password: string;
  nextPath: string;
  goals?: SignupGoals;
}): void {
  const resolvedGoals =
    goals ??
    (() => {
      if (nextPath.includes('/join')) {
        try {
          const q = new URL(nextPath, 'https://www.shopgnd.gd').searchParams.get('intent');
          const fromIntent = parseSignupGoalsFromIntent(q);
          if (fromIntent.sell || fromIntent.pro || fromIntent.serviceIntent) return fromIntent;
        } catch {
          /* ignore */
        }
        return getStoredSignupGoals() ?? { sell: true, pro: false, serviceIntent: null };
      }
      if (nextPath.includes('/pro/onboard')) {
        return {
          sell: nextPath.includes('/vendor/onboard'),
          pro: true,
          serviceIntent: 'trades' as const,
        };
      }
      if (nextPath.startsWith('/vendor/onboard')) {
        return getStoredSignupGoals() ?? { sell: true, pro: false, serviceIntent: null };
      }
      return SHOP_ONLY_GOALS;
    })();
  persistSignupNavigation(email, resolvedGoals, nextPath, undefined, password);
}

export function getStoredSignupEmail(): string | null {
  try {
    return sessionStorage.getItem(EMAIL_KEY) ?? localStorage.getItem(LS_EMAIL_KEY);
  } catch {
    return null;
  }
}

export function getStoredSignupPassword(): string | null {
  try {
    return sessionStorage.getItem(PASSWORD_KEY) ?? localStorage.getItem(LS_PASSWORD_KEY);
  } catch {
    return null;
  }
}

export function clearStoredSignupPassword(): void {
  try {
    sessionStorage.removeItem(PASSWORD_KEY);
    localStorage.removeItem(LS_PASSWORD_KEY);
  } catch {
    /* ignore */
  }
}

export function getStoredSignupGoals(): SignupGoals | null {
  try {
    const raw = sessionStorage.getItem(GOALS_KEY) ?? localStorage.getItem(LS_GOALS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SignupGoals;
    if (typeof parsed.sell !== 'boolean' || typeof parsed.pro !== 'boolean') return null;
    return {
      sell: parsed.sell,
      pro: parsed.pro,
      serviceIntent:
        parsed.serviceIntent === undefined || parsed.serviceIntent === null
          ? null
          : parsed.serviceIntent,
    };
  } catch {
    return null;
  }
}

export function getPendingPostSignupPath(): string | null {
  try {
    const raw = sessionStorage.getItem(NEXT_KEY) ?? localStorage.getItem(LS_NEXT_KEY);
    return sanitizeLoginNextPath(raw);
  } catch {
    return null;
  }
}

/** Drop "also list services" from stored goals so browsing is not treated as incomplete pro signup. */
export function clearOptionalProSignupGoal(): void {
  try {
    const goals = getStoredSignupGoals();
    if (!goals?.pro) return;
    const kept: SignupGoals = { sell: goals.sell, pro: false, serviceIntent: goals.serviceIntent };
    sessionStorage.setItem(GOALS_KEY, JSON.stringify(kept));
    localStorage.setItem(LS_GOALS_KEY, JSON.stringify(kept));
  } catch {
    /* private mode */
  }
}

/** Stops PostSignupRedirect from hijacking navigation; keeps signup goals for optional pro setup links. */
export function releaseForcedPostSignupRedirects(): void {
  try {
    sessionStorage.removeItem(NEXT_KEY);
    localStorage.removeItem(LS_NEXT_KEY);
    sessionStorage.removeItem(BUSINESS_NAME_KEY);
    localStorage.removeItem(LS_BUSINESS_NAME_KEY);
  } catch {
    /* private mode */
  }
}

export function clearPendingSignupNavigation(): void {
  try {
    sessionStorage.removeItem(NEXT_KEY);
    sessionStorage.removeItem(GOALS_KEY);
    sessionStorage.removeItem(EMAIL_KEY);
    sessionStorage.removeItem(BUSINESS_NAME_KEY);
    localStorage.removeItem(LS_GOALS_KEY);
    localStorage.removeItem(LS_NEXT_KEY);
    localStorage.removeItem(LS_EMAIL_KEY);
    localStorage.removeItem(LS_BUSINESS_NAME_KEY);
    clearStoredSignupPassword();
  } catch {
    /* ignore */
  }
}

/** True when signup intent includes pro but no ServiceProvider row exists yet. */
export function needsProOnboarding(
  pro: { id: string } | null | undefined,
  goals?: SignupGoals | null,
): boolean {
  const g = goals ?? getStoredSignupGoals();
  return Boolean(g?.pro && !pro);
}

/**
 * After a Vendor row exists: clear forced "next" redirects (pro setup stays optional on Done / hub).
 *
 * Call only when the pro profile query has finished (`proProfileReady`) so sell+pro intent is not
 * cleared while `pro` is still loading.
 */
export function syncSignupNavigationAfterVendorComplete(
  pro: { id: string } | null | undefined,
): void {
  releaseForcedPostSignupRedirects();
  const goals = getStoredSignupGoals();
  if (!goals) return;
  if (!needsProOnboarding(pro, goals)) {
    clearPendingSignupNavigation();
    return;
  }
  // Vendor row exists — stop treating the user as mid vendor signup (avoids /vendor/onboard loops).
  try {
    const kept: SignupGoals = { sell: false, pro: goals.pro, serviceIntent: goals.serviceIntent };
    sessionStorage.setItem(GOALS_KEY, JSON.stringify(kept));
    localStorage.setItem(LS_GOALS_KEY, JSON.stringify(kept));
  } catch {
    /* private mode */
  }
}

/** Where to send the user after confirm / sign-in (onboarding before generic hub). */
function normalizePostAuthPending(pending: string): string {
  const safe = sanitizeLoginNextPath(pending);
  if (!safe || safe === '/') return '/';
  if (safe.includes('/pro/onboard') && !safe.startsWith('/pro/onboard') && !isJoinPath(safe.split('?')[0] ?? '')) {
    return '/join?intent=pro';
  }
  if (safe.startsWith('/vendor/onboard')) {
    return safe.replace(/^\/vendor\/onboard/, JOIN_PATH);
  }
  if (safe.startsWith('/pro/onboard')) {
    const qs = safe.includes('?') ? safe.slice(safe.indexOf('?')) : '';
    const params = new URLSearchParams(qs.startsWith('?') ? qs.slice(1) : qs);
    if (!params.get('intent')) params.set('intent', 'pro');
    const out = params.toString();
    return out ? `${JOIN_PATH}?${out}` : `${JOIN_PATH}?intent=pro`;
  }
  return safe;
}

/** Same precedence as `resolveAuthReturnPath` in authRedirect.ts (explicit next, then stored). */
export function resolveDestinationAfterAuth(
  user: AuthUser,
  explicitNext?: string | null,
): string {
  // Leftover signup state from creating another account must not hijack this session.
  const storedEmail = getStoredSignupEmail();
  if (
    storedEmail &&
    storedEmail.trim().toLowerCase() !== user.email.trim().toLowerCase()
  ) {
    clearPendingSignupNavigation();
  }

  // Admins go to the hub unless the login URL explicitly requested another path.
  // Stale sell/pro goals (from /join while testing) must not send them to onboarding.
  if (rolesIncludeAdmin(user.roles)) {
    const explicit = sanitizeLoginNextPath(explicitNext);
    if (explicit && explicit !== '/') {
      const pathOnly = explicit.split('?')[0] ?? explicit;
      // Never land staff on the seller/pro join funnel (creates accidental storefronts).
      if (
        isJoinPath(pathOnly) ||
        pathOnly.startsWith('/vendor/onboard') ||
        pathOnly.startsWith('/pro/onboard')
      ) {
        clearPendingSignupNavigation();
        return defaultPathForRoles(user.roles);
      }
      return normalizePostAuthPending(explicit);
    }
    clearPendingSignupNavigation();
    return defaultPathForRoles(user.roles);
  }

  const pendingRaw =
    sanitizeLoginNextPath(explicitNext) ?? getPendingPostSignupPath();
  if (pendingRaw && pendingRaw !== '/') {
    return normalizePostAuthPending(pendingRaw);
  }

  const goals = getStoredSignupGoals() ?? SHOP_ONLY_GOALS;
  if (goals.sell || goals.pro) {
    return resolvePostSignupPath(goals, null, null);
  }

  return defaultPathForRoles(user.roles);
}

/** Adjusts stored navigation when a Vendor row already exists (keeps sell+pro → pro intent). */
export async function resolveDestinationAfterAuthWithVendorCheck(
  user: AuthUser,
  explicitNext?: string | null,
): Promise<string> {
  const goals = getStoredSignupGoals();
  let effectiveNext = explicitNext ?? null;
  try {
    const owned = await getVendorForOwnerUser(user.id);
    if (owned) {
      const pro = goals?.pro
        ? ((await getServiceProviderForOwnerUser(user.id)) ?? null)
        : null;
      syncSignupNavigationAfterVendorComplete(pro);
      const vendorOnboard = JOIN_PATH;
      if (vendorAwaitingAdminReview(owned.approvalStatus)) {
        if (
          !effectiveNext ||
          effectiveNext === '/' ||
          effectiveNext.startsWith(vendorOnboard) ||
          effectiveNext.startsWith('/vendor/onboard')
        ) {
          effectiveNext = '/hub/home';
        }
      } else if (vendorIsApproved(owned.approvalStatus)) {
        if (
          !effectiveNext ||
          effectiveNext === '/' ||
          effectiveNext.startsWith(vendorOnboard) ||
          effectiveNext.startsWith('/vendor/onboard') ||
          effectiveNext.startsWith('/pro/onboard') ||
          effectiveNext.includes('intent=pro')
        ) {
          effectiveNext = '/hub/home';
        }
      }
    }
  } catch {
    /* offline / API unavailable — fall back to stored navigation */
  }
  return resolveDestinationAfterAuth(user, effectiveNext);
}

/** True when the user still needs to complete vendor onboarding (no Vendor row yet). */
export function needsVendorOnboarding(vendor: { id: string } | null | undefined): boolean {
  return !vendor;
}

export function destinationForIncompleteVendorOnboarding(
  vendor?: { id: string } | null,
): string | null {
  if (!needsVendorOnboarding(vendor)) return null;
  const goals = getStoredSignupGoals();
  const pending = getPendingPostSignupPath();
  if (pending?.startsWith(JOIN_PATH) || pending?.startsWith('/vendor/onboard')) {
    return normalizePostAuthPending(pending);
  }
  if (goals?.sell) return resolvePostSignupPath(goals, null, null);
  return null;
}

/** Only when the user explicitly started pro onboarding (stored next), not from signup goals alone. */
export function destinationForIncompleteProOnboarding(
  pro?: { id: string } | null,
): string | null {
  if (pro) return null;
  const pending = getPendingPostSignupPath();
  if (pending?.startsWith(JOIN_PATH) && pending.includes('intent=pro')) return pending;
  if (pending?.startsWith('/pro/onboard')) return normalizePostAuthPending(pending);
  if (pending?.includes('/pro/onboard')) return `${JOIN_PATH}?intent=pro`;
  return null;
}
